import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Avatar, Chip,
  List, ListItem, ListItemText, Divider, Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StatCard from '../components/common/StatCard';
import StatusBadge from '../components/common/StatusBadge';
import { useAuth } from '../context/AuthContext';
import db from '../data/demoDatabase';

const quickActions = [
  { label: 'Browse Products', path: '/products', icon: AccountBalanceIcon, color: '#1B365D' },
  { label: 'Apply Now', path: '/apply', icon: AssignmentIcon, color: '#00B894' },
  { label: 'Invite Friends', path: '/invite', icon: PeopleIcon, color: '#7B1FA2' },
  { label: 'Redeem Tokens', path: '/redemption', icon: StarIcon, color: '#FFD700' },
];

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const yr = Math.floor(mo / 12);
  return `${yr} year${yr === 1 ? '' : 's'} ago`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [refreshKey] = useState(0);
  const userName = user?.name?.split(' ')[0] || 'User';
  const userLevel = user?.referralLevel || 'Silver';
  const userTokens = user?.tokenBalance || 0;

  const userCases = useMemo(
    () => (user ? db.cases.getByUserId(user.id) : []),
    [user, refreshKey]
  );

  const stats = useMemo(() => {
    const totalApplications = userCases.filter((c) => c.applicantId === user?.id).length;
    const referralCases = userCases.filter(
      (c) => c.referralOwnerId === user?.id && c.applicantId !== user?.id
    );
    const approvedCount = userCases.filter((c) =>
      ['Approved', 'Disbursed'].includes(c.status)
    ).length;
    const userNotifs = user ? db.notifications.getByUserId(user.id) : [];
    const totalTokensEarned = userNotifs
      .filter((n) => n.type === 'token')
      .reduce((sum, n) => sum + (Number(n.amount) || Number(n.metadata?.amount) || 0), 0);
    return {
      totalApplications,
      referralCount: referralCases.length,
      approvedCount,
      totalTokensEarned,
    };
  }, [userCases, user, refreshKey]);

  const recentActivity = useMemo(() => {
    return [...userCases]
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .slice(0, 4)
      .map((c) => ({
        id: c.id,
        title: 'Application Status',
        subtitle: c.productName,
        status: c.status,
        date: relativeTime(c.updatedAt),
      }));
  }, [userCases]);

  const featuredProducts = useMemo(
    () =>
      db.products
        .getAll()
        .slice(0, 3)
        .map((p) => ({
          name: p.name,
          bank: p.bank || '—',
          rate: p.profitRate != null ? `${p.profitRate}% p.a.` : '—',
          icon: AccountBalanceIcon,
        })),
    [refreshKey]
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Banner */}
      <Card sx={{
        borderRadius: 4, mb: 3, overflow: 'hidden',
        background: 'linear-gradient(135deg, #1B365D 0%, #2C5282 60%, #00B894 100%)',
      }}>
        <CardContent sx={{ p: 4, color: 'white' }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Welcome back, {userName}!</Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, mb: 2 }}>
                Your referral level is <strong>{userLevel}</strong>. You have <strong>{userTokens} tokens</strong> available.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button variant="contained" onClick={() => navigate('/products')}
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, textTransform: 'none', fontWeight: 600,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
                  Browse Products
                </Button>
                <Button variant="outlined" onClick={() => navigate('/referral-links')}
                  sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', borderRadius: 2, textTransform: 'none',
                    '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                  Share Referral Link
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar sx={{ width: 72, height: 72, backgroundColor: 'rgba(255,255,255,0.2)', mb: 1 }}>
                  <StarIcon sx={{ fontSize: 40, color: '#FFD700' }} />
                </Avatar>
                <Chip label={`${userLevel} Member`} sx={{ backgroundColor: 'rgba(255,215,0,0.3)', color: '#FFD700', fontWeight: 700 }} />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Total Applications"
            value={String(stats.totalApplications)}
            subtitle={`${stats.approvedCount} approved`}
            icon={AssignmentIcon}
            color="#1B365D"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Referrals"
            value={String(stats.referralCount)}
            subtitle="people referred"
            icon={PeopleIcon}
            color="#7B1FA2"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Token Balance"
            value={String(userTokens)}
            subtitle="available"
            icon={StarIcon}
            color="#FFD700"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Rewards Earned"
            value={`RM ${stats.totalTokensEarned}`}
            subtitle="total value"
            icon={TrendingUpIcon}
            color="#00B894"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Quick Actions</Typography>
          <Grid container spacing={2}>
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Grid item xs={6} key={action.label}>
                  <Card
                    onClick={() => navigate(action.path)}
                    sx={{
                      borderRadius: 3, cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Avatar sx={{ width: 48, height: 48, mx: 'auto', mb: 1.5, backgroundColor: `${action.color}15` }}>
                        <Icon sx={{ color: action.color }} />
                      </Avatar>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{action.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Recent Activity</Typography>
            <Button size="small" endIcon={<ArrowForwardIcon />} sx={{ textTransform: 'none' }}>View All</Button>
          </Box>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            {recentActivity.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="info">No data yet</Alert>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {recentActivity.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    <ListItem sx={{ px: 3, py: 1.5 }}>
                      <ListItemText
                        primary={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{item.title}</Typography>}
                        secondary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{item.subtitle} · {item.date}</Typography>
                            <StatusBadge status={item.status} />
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Card>
        </Grid>

        {/* Featured Products */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Featured Products</Typography>
            <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/products')} sx={{ textTransform: 'none' }}>
              View All
            </Button>
          </Box>
          <Grid container spacing={3}>
            {featuredProducts.map((product) => {
              const Icon = product.icon;
              return (
                <Grid item xs={12} md={4} key={product.name}>
                  <Card sx={{
                    borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', cursor: 'pointer',
                    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }, transition: 'all 0.2s',
                  }}
                    onClick={() => navigate('/products')}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <Avatar sx={{ backgroundColor: '#E3F2FD' }}>
                          <Icon sx={{ color: '#1B365D' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{product.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{product.bank}</Typography>
                        </Box>
                      </Box>
                      <Chip label={product.rate} size="small" sx={{ fontWeight: 600, backgroundColor: '#E8F5E9', color: '#2E7D32' }} />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
